import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import {
  GUIDE_TITLE_PLACEHOLDER,
  GUIDE_TITLE_PLACEHOLDER_ID,
  parseGuideTitleHtml,
  serializeGuideTitleHtml
} from './guidesUtils';

const HEADING_OPTIONS = [
  { value: 1, label: 'H1' },
  { value: 2, label: 'H2' },
  { value: 3, label: 'H3' },
  { value: 4, label: 'H4' },
  { value: 5, label: 'H5' },
  { value: 6, label: 'H6' }
];

function titleToggleOutsideDocument(menuEl) {
  const toggle = menuEl?.querySelector?.('.guides-title-level-toggle') || menuEl;
  const scroller = toggle?.closest?.('.rte-document-body');
  if (!toggle || !scroller) return false;
  const page = toggle.closest('.rte-document-page');
  const toggleRect = toggle.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const pageRect = page ? page.getBoundingClientRect() : scrollerRect;
  const top = Math.max(scrollerRect.top, pageRect.top);
  const bottom = Math.min(scrollerRect.bottom, pageRect.bottom);
  return toggleRect.bottom < top + 4 || toggleRect.top > bottom - 4;
}

function isTitleVisuallyEmpty(el) {
  if (!el) return true;
  const text = String(el.textContent || '').replace(/\u00a0/g, ' ').trim();
  return !text;
}

/**
 * Isolated document title. Heading level (H1–H6) does not affect topic hierarchy.
 */
export default function GuideTitleEditor({
  value,
  onChange,
  placeholder = GUIDE_TITLE_PLACEHOLDER,
  onRequestFocusBody
}) {
  const hostRef = useRef(null);
  const lastEmittedRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const parsed = parseGuideTitleHtml(value);
  const level = parsed.level;
  const Tag = `h${level}`;

  const emitFromDomRef = useRef(null);

  const emitFromDom = (nextLevel = level) => {
    const el = hostRef.current;
    if (!el) return;
    const empty = isTitleVisuallyEmpty(el);
    if (empty) {
      el.setAttribute('data-guides-placeholder', placeholder);
    } else {
      el.removeAttribute('data-guides-placeholder');
    }
    const html = serializeGuideTitleHtml({
      level: nextLevel,
      innerHtml: el.innerHTML,
      text: el.textContent
    });
    lastEmittedRef.current = html;
    onChange?.(html);
  };
  emitFromDomRef.current = emitFromDom;

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const incoming = parseGuideTitleHtml(value);
    const sameEmit = lastEmittedRef.current != null && value === lastEmittedRef.current;
    const innerMatches = el.innerHTML === incoming.innerHtml
      || (incoming.empty && isTitleVisuallyEmpty(el));
    if (!sameEmit || !innerMatches) {
      if (!innerMatches) el.innerHTML = incoming.innerHtml || '<br>';
    }
    if (incoming.empty) {
      el.setAttribute('data-guides-placeholder', placeholder);
    } else {
      el.removeAttribute('data-guides-placeholder');
    }
  }, [value, placeholder, level]);

  useEffect(() => {
    const closeIfOutside = () => {
      const menu = hostRef.current?.closest('.guides-title-editor')?.querySelector('.guides-title-level-menu');
      if (titleToggleOutsideDocument(menu)) setMenuOpen(false);
    };
    window.addEventListener('scroll', closeIfOutside, true);
    return () => window.removeEventListener('scroll', closeIfOutside, true);
  }, []);

  useLayoutEffect(() => {
    const wrap = hostRef.current?.closest('.guides-title-editor');
    if (!wrap) return undefined;
    const onApply = (e) => {
      const n = Number(e.detail?.level);
      if (n >= 1 && n <= 6) emitFromDomRef.current?.(n);
    };
    wrap.addEventListener('guide-title-apply-heading', onApply);
    return () => wrap.removeEventListener('guide-title-apply-heading', onApply);
  }, []);

  const setLevel = (nextLevel) => {
    const n = Number(nextLevel);
    if (n === level || n < 1 || n > 6) return;
    emitFromDom(n);
  };

  return (
    <div className="guides-title-editor">
      <Dropdown
        align="start"
        className="guides-title-level-menu"
        show={menuOpen}
        onToggle={(next) => {
          const menu = hostRef.current?.closest('.guides-title-editor')?.querySelector('.guides-title-level-menu');
          if (next && titleToggleOutsideDocument(menu)) {
            setMenuOpen(false);
            return;
          }
          setMenuOpen(Boolean(next));
        }}
        onSelect={(eventKey) => setLevel(eventKey)}
      >
        <Dropdown.Toggle
          variant="light-lite"
          size="sm"
          className="guides-title-level-toggle no-arrow"
          aria-label="Title heading level"
          title="Title heading level"
        >
          <span className="font-monospace fw-semibold">{`H${level}`}</span>
        </Dropdown.Toggle>
        <Dropdown.Menu className="shadow-sm">
          {HEADING_OPTIONS.map((opt) => (
            <Dropdown.Item
              key={opt.value}
              eventKey={String(opt.value)}
              as="button"
              type="button"
              active={opt.value === level}
            >
              {opt.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
      <Tag
        ref={hostRef}
        id={GUIDE_TITLE_PLACEHOLDER_ID}
        data-guides-title="1"
        className="guides-title-editor-input"
        contentEditable
        suppressContentEditableWarning
        spellCheck
        role="textbox"
        aria-label="Document title"
        data-guides-placeholder={parsed.empty ? placeholder : undefined}
        onInput={() => emitFromDom(level)}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData?.getData('text/plain') || '';
          document.execCommand('insertText', false, text.replace(/\s*\n+\s*/g, ' '));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onRequestFocusBody?.();
          }
        }}
      />
    </div>
  );
}
